import React, { ReactNode, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import '@src/components/FilterDropDown/FilterDropdown.less';
import {
  DEFAULT_SECONDARY_PANEL_WIDTH,
  useSecondaryPanelFilters,
  useSecondaryPanelUI,
} from '@src/components/SecondaryPanel/context/SecondaryPanelContext';
import { useIsMobile } from '@src/packages/utils';
import FilterTagItem from './components/FilterTagItem';
import styles from './FilterActiveTags.module.less';
import {
  FieldValue,
  FilterValues,
  SetValueProps,
} from '../FilterDropDown/FilterDropDown.interfaces';

const shouldShowFilterTag = (
  value: FieldValue,
  valueType?: FilterValues[string]['valueType'],
  clearValue?: FieldValue,
  hideWhenCleared?: boolean,
): boolean => {
  if (hideWhenCleared && value === clearValue) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (valueType === 'boolean') {
    return typeof value === 'boolean';
  }

  if (valueType === 'null') {
    return value === null;
  }

  if (valueType === 'number') {
    return typeof value === 'number';
  }

  return Boolean(value);
};
export interface FilterActiveTagsProps {
  className?: string;
  onAddFilter?: () => void;
  sticky?: boolean;
  stickyTop?: number;
  children?: ReactNode;
  scrollRegionClassName?: string;
  customValues?: FilterValues;
  /** When `customValues` is the source of truth, wire clears here (otherwise clears update the secondary panel only). */
  onCustomFilterChange?: (props: SetValueProps) => void;
}

export interface FilterActiveTagsContainerProps {
  children?: ReactNode;
  className?: string;
  /** Distance from viewport top when stuck (e.g. height of a sticky toolbar above this block). */
  stickyTop?: number;
  style?: React.CSSProperties;
  /** Page-level filter bar the secondary panel aligns to; disable for nested (e.g. per-widget) bars. */
  panelAnchor?: boolean;
}

export interface FilterActiveTagsBarProps {
  children?: ReactNode;
  className?: string;
}

export interface FilterActiveTagsScrollAreaProps {
  children?: ReactNode;
  className?: string;
}

export interface FilterActiveTagsTrailingProps {
  children?: ReactNode;
  className?: string;
}

export interface AddFilterButtonProps {
  onAddFilter: () => void;
  className?: string;
}

export const AddFilterButton: React.FC<AddFilterButtonProps> = ({ onAddFilter, className }) => {
  const intl = useIntl();
  return (
    <div
      className={classNames('filter-dropdown-add-filter-button', className)}
      onClick={e => {
        e.stopPropagation();
        onAddFilter();
      }}
    >
      <span className="material-symbols-outlined">add</span>
      <span>{intl.formatMessage({ id: 'filterDropdown.addFilter' })}</span>
    </div>
  );
};

const FilterActiveTagsRoot: React.FC<FilterActiveTagsProps> = ({
  className = '',
  onAddFilter,
  sticky = false,
  stickyTop = 0,
  children,
  scrollRegionClassName = '',
  customValues = {} as FilterValues,
  onCustomFilterChange,
}) => {
  const { setValue, values: secondaryPanelValues } = useSecondaryPanelFilters();
  const clearFilterValue = onCustomFilterChange ?? setValue;
  const values = useMemo(() => {
    if (Object.keys(customValues).length) {
      return customValues;
    }
    return secondaryPanelValues;
  }, [customValues, secondaryPanelValues]);

  const tags = useMemo(() => {
    return Object.entries(values)
      .map(([key, { value, label, valueTranslated, valueType, clearValue, hideWhenCleared }]) => ({
        key,
        value,
        label,
        valueTranslated,
        valueType,
        clearValue,
        hideWhenCleared,
      }))
      .filter(row =>
        shouldShowFilterTag(row.value, row.valueType, row.clearValue, row.hideWhenCleared),
      )
      .map(row => ({
        key: row.key,
        label: (
          <>
            <span className="filter-dropdown-filter-content-tag-item-label">{row.label}:</span>
            <span className="filter-dropdown-filter-content-tag-item-value">
              {row.valueTranslated || row.value}
            </span>
          </>
        ),
        valueType: row.valueType,
        clearValue: row.clearValue,
      }));
  }, [values]);

  const tagsRow = (
    <div
      data-testid="filter-active-tags"
      className={classNames(
        'filter-dropdown-filters-container',
        styles.tags,
        sticky && 'filter-dropdown-filters-container--sticky',
        className,
      )}
      style={sticky ? { top: stickyTop } : undefined}
    >
      {tags.map(tag => (
        <FilterTagItem
          key={tag.key}
          filterKey={tag.key}
          label={tag.label}
          onClear={clearFilterValue}
          valueType={tag.valueType}
          clearValue={tag.clearValue}
        />
      ))}
      {onAddFilter && <AddFilterButton onAddFilter={onAddFilter} className={styles.addFilter} />}
    </div>
  );

  if (children !== undefined && children !== null) {
    if (sticky) {
      return (
        <div
          className={classNames(
            'filter-dropdown-sticky-section',
            'filter-dropdown-sticky-section--scroll',
            scrollRegionClassName,
          )}
        >
          {tagsRow}
          {children}
        </div>
      );
    }

    return (
      <>
        {tagsRow}
        {children}
      </>
    );
  }

  return tagsRow;
};

const FilterActiveTagsContainer = React.forwardRef<
  HTMLDivElement,
  FilterActiveTagsContainerProps
>(({ children, className, stickyTop = 0, style, panelAnchor = true }, ref) => {
  const { isOpen, panel, setAnchorNode } = useSecondaryPanelUI();
  const { isMobile } = useIsMobile();
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const reservePanelSpace = panelAnchor && isOpen && Boolean(panel.content) && !isMobile;

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useLayoutEffect(() => {
    if (!panelAnchor) return undefined;

    const node = nodeRef.current;
    setAnchorNode(node);
    return () => setAnchorNode(prev => (prev === node ? null : prev));
  }, [panelAnchor, setAnchorNode]);

  return (
    <div
      ref={setRefs}
      className={classNames(styles.container, reservePanelSpace && styles.withPanel, className)}
      style={{
        top: stickyTop,
        ...(reservePanelSpace
          ? {
              ['--secondary-panel-width' as string]: `${
                panel.width ?? DEFAULT_SECONDARY_PANEL_WIDTH
              }px`,
            }
          : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
});
FilterActiveTagsContainer.displayName = 'FilterActiveTagsContainer';

const FilterActiveTagsBar: React.FC<FilterActiveTagsBarProps> = ({ children, className }) => (
  <div className={classNames(styles.bar, className)}>{children}</div>
);

const FilterActiveTagsScrollArea: React.FC<FilterActiveTagsScrollAreaProps> = ({
  children,
  className,
}) => <div className={classNames(styles.scrollArea, className)}>{children}</div>;

const FilterActiveTagsTrailing: React.FC<FilterActiveTagsTrailingProps> = ({
  children,
  className,
}) => <div className={classNames(styles.trailing, className)}>{children}</div>;

type FilterActiveTagsComponent = React.FC<FilterActiveTagsProps> & {
  Container: typeof FilterActiveTagsContainer;
  Bar: typeof FilterActiveTagsBar;
  ScrollArea: typeof FilterActiveTagsScrollArea;
  Trailing: typeof FilterActiveTagsTrailing;
};
const FilterActiveTags = FilterActiveTagsRoot as FilterActiveTagsComponent;
FilterActiveTags.Container = FilterActiveTagsContainer;
FilterActiveTags.Bar = FilterActiveTagsBar;
FilterActiveTags.ScrollArea = FilterActiveTagsScrollArea;
FilterActiveTags.Trailing = FilterActiveTagsTrailing;
export default FilterActiveTags;
